/**
 * Comprehensive Carrier Report PDF Generator
 * Creates professional multi-page reports with charts and detailed analysis
 */

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
// Type declarations are automatically picked up from src/types/jspdf-autotable.d.ts
import { generateFilename } from './csv-export';
import type { Violation, Inspection, Crash, Vehicle } from '../types';

// Platform brand colors
const COLORS = {
  primary: [37, 99, 235] as [number, number, number],
  danger: [220, 38, 38] as [number, number, number],
  warning: [245, 158, 11] as [number, number, number],
  success: [34, 197, 94] as [number, number, number],
  gray: [107, 114, 128] as [number, number, number],
  dark: [17, 24, 39] as [number, number, number],
  lightGray: [243, 244, 246] as [number, number, number],
};

interface CarrierProfile {
  legalName?: string;
  dotNumber?: string | number;
  [key: string]: unknown;
}

interface ReportData {
  profile: CarrierProfile;
  violations: Violation[];
  inspections: Inspection[];
  crashes: Crash[];
  basics: any[];  // FMCSA BASIC scores with nested structure
  vehicles?: Vehicle[];
  healthScore?: number;
  observedUnits?: any;
  census?: any;
}

/**
 * Generate comprehensive carrier report
 */
export const generateCarrierReport = async (
  data: ReportData,
  onProgress?: (progress: number, message: string) => void
): Promise<void> => {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'letter',
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margins = { left: 14, right: 14, top: 15, bottom: 20 };

  if (onProgress) onProgress(5, 'Starting report generation...');

  // Page 1: Cover Page
  addCoverPage(doc, data, pageWidth, pageHeight);

  // Page 2: Executive Summary
  doc.addPage();
  if (onProgress) onProgress(15, 'Adding executive summary...');
  addExecutiveSummary(doc, data, margins);

  // Page 3: BASIC Scores Analysis
  doc.addPage();
  if (onProgress) onProgress(30, 'Adding BASIC scores analysis...');
  addBASICAnalysis(doc, data, margins);

  // Page 4: Violation Analysis
  doc.addPage();
  if (onProgress) onProgress(45, 'Adding violation analysis...');
  addViolationAnalysis(doc, data, margins);

  // Page 5: Crash History
  doc.addPage();
  if (onProgress) onProgress(60, 'Adding crash history...');
  addCrashHistory(doc, data, margins);

  // Page 6: Inspection Summary
  doc.addPage();
  if (onProgress) onProgress(75, 'Adding inspection summary...');
  addInspectionSummary(doc, data, margins);

  // Page 7: Recommendations
  doc.addPage();
  if (onProgress) onProgress(90, 'Adding recommendations...');
  addRecommendations(doc, data, margins);

  // Add page numbers to all pages
  addPageNumbers(doc);

  if (onProgress) onProgress(100, 'Report complete!');

  // Save the document
  const filename = generateFilename(
    'comprehensive_report',
    data.profile?.legalName,
    'pdf'
  );
  doc.save(filename);
};

/**
 * Add cover page
 */
const addCoverPage = (
  doc: jsPDF,
  data: ReportData,
  pageWidth: number,
  pageHeight: number
): void => {
  // Background header block
  doc.setFillColor(...COLORS.primary);
  doc.rect(0, 0, pageWidth, 80, 'F');

  // Title
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(28);
  doc.setFont('helvetica', 'bold');
  doc.text('CARRIER SAFETY REPORT', pageWidth / 2, 35, { align: 'center' });

  // Subtitle
  doc.setFontSize(14);
  doc.setFont('helvetica', 'normal');
  doc.text('Comprehensive Safety Analysis', pageWidth / 2, 48, { align: 'center' });

  // Carrier name
  doc.setTextColor(...COLORS.dark);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  const carrierName = data.profile?.legalName || 'Unknown Carrier';
  doc.text(carrierName, pageWidth / 2, 110, { align: 'center' });

  // DOT Number
  doc.setFontSize(16);
  doc.setTextColor(...COLORS.gray);
  doc.setFont('helvetica', 'normal');
  doc.text(`DOT# ${data.profile?.dotNumber || 'N/A'}`, pageWidth / 2, 122, { align: 'center' });

  // Report generation info box
  doc.setFillColor(...COLORS.lightGray);
  doc.roundedRect(pageWidth / 2 - 50, 140, 100, 35, 3, 3, 'F');

  doc.setFontSize(10);
  doc.setTextColor(...COLORS.gray);
  doc.text('Report Generated', pageWidth / 2, 150, { align: 'center' });

  doc.setFontSize(12);
  doc.setTextColor(...COLORS.dark);
  doc.setFont('helvetica', 'bold');
  doc.text(new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }), pageWidth / 2, 160, { align: 'center' });

  doc.setFontSize(9);
  doc.setTextColor(...COLORS.gray);
  doc.setFont('helvetica', 'normal');
  doc.text(new Date().toLocaleTimeString(), pageWidth / 2, 168, { align: 'center' });

  // Quick stats boxes
  const stats = [
    { label: 'Violations', value: data.violations?.length || 0, color: COLORS.danger },
    { label: 'Inspections', value: data.inspections?.length || 0, color: COLORS.primary },
    { label: 'Crashes', value: data.crashes?.length || 0, color: COLORS.warning },
    { label: 'BASIC Categories', value: data.basics?.length || 0, color: COLORS.success },
  ];

  const boxWidth = 45;
  const boxHeight = 30;
  const startX = (pageWidth - (stats.length * boxWidth + (stats.length - 1) * 8)) / 2;
  const startY = 195;

  stats.forEach((stat, idx) => {
    const x = startX + idx * (boxWidth + 8);

    doc.setFillColor(...stat.color);
    doc.roundedRect(x, startY, boxWidth, boxHeight, 2, 2, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(stat.value.toString(), x + boxWidth / 2, startY + 13, { align: 'center' });

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(stat.label, x + boxWidth / 2, startY + 22, { align: 'center' });
  });

  // Footer
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.gray);
  doc.setFont('helvetica', 'italic');
  doc.text('Transportation Safety Platform', pageWidth / 2, pageHeight - 25, { align: 'center' });
  doc.text('This report is confidential and intended for authorized use only.', pageWidth / 2, pageHeight - 18, { align: 'center' });
};

/**
 * Add executive summary page
 */
const addExecutiveSummary = (
  doc: jsPDF,
  data: ReportData,
  margins: { left: number; right: number; top: number; bottom: number }
): void => {
  let yPos = margins.top;

  // Section header
  yPos = addSectionHeader(doc, 'EXECUTIVE SUMMARY', yPos, margins.left);
  yPos += 10;

  // Carrier profile table
  doc.setFontSize(12);
  doc.setTextColor(...COLORS.primary);
  doc.setFont('helvetica', 'bold');
  doc.text('Carrier Profile', margins.left, yPos);
  yPos += 6;

  const profile = data.profile || {};
  const profileData = [
    ['Legal Name', profile.legalName || 'N/A'],
    ['DBA Name', profile.dbaName || 'N/A'],
    ['DOT Number', profile.dotNumber?.toString() || 'N/A'],
    ['Operating Status', profile.allowedToOperate === 'Y' ? 'Authorized to Operate' : 'Not Authorized'],
    ['Safety Rating', profile.safetyRating || 'Unrated'],
    ['Total Drivers', profile.totalDrivers?.toString() || 'N/A'],
    ['Total Power Units', profile.totalPowerUnits?.toString() || 'N/A'],
    ['Physical Address', `${profile.phyStreet || ''}, ${profile.phyCity || ''}, ${profile.phyState || ''} ${profile.phyZipcode || ''}`.trim().replace(/^,\s*/, '')],
  ];

  autoTable(doc, {
    startY: yPos,
    head: [],
    body: profileData,
    theme: 'plain',
    styles: { fontSize: 9, cellPadding: 2 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 45, textColor: COLORS.gray },
      1: { cellWidth: 100, textColor: COLORS.dark },
    },
  });

  yPos = doc.lastAutoTable.finalY + 12;

  // Safety metrics summary
  doc.setFontSize(12);
  doc.setTextColor(...COLORS.primary);
  doc.setFont('helvetica', 'bold');
  doc.text('Safety Metrics Overview', margins.left, yPos);
  yPos += 6;

  // Calculate metrics
  const oosViolations = data.violations?.filter(v => v.outOfServiceIndicator === 'Y').length || 0;
  const criticalViolations = data.violations?.filter(v => v.severity === 'CRITICAL').length || 0;
  const fatalCrashes = data.crashes?.filter(c => Number(c.fatalities || 0) > 0).length || 0;
  const injuryCrashes = data.crashes?.filter(c => Number(c.injuries || 0) > 0).length || 0;

  const metricsData = [
    ['Total Violations (Last 500 Inspections)', data.violations?.length?.toString() || '0'],
    ['Out-of-Service Violations', oosViolations.toString()],
    ['Critical Violations', criticalViolations.toString()],
    ['Total Crashes', data.crashes?.length?.toString() || '0'],
    ['Fatal Crashes', fatalCrashes.toString()],
    ['Injury Crashes', injuryCrashes.toString()],
    ['Driver OOS Rate', `${profile.driverOosRate || 0}%`],
    ['Vehicle OOS Rate', `${profile.vehicleOosRate || 0}%`],
  ];

  autoTable(doc, {
    startY: yPos,
    head: [],
    body: metricsData,
    theme: 'striped',
    styles: { fontSize: 9, cellPadding: 3 },
    columnStyles: {
      0: { cellWidth: 80 },
      1: { cellWidth: 40, halign: 'center', fontStyle: 'bold' },
    },
    alternateRowStyles: { fillColor: COLORS.lightGray },
  });

  yPos = doc.lastAutoTable.finalY + 12;

  // Risk assessment
  doc.setFontSize(12);
  doc.setTextColor(...COLORS.primary);
  doc.setFont('helvetica', 'bold');
  doc.text('Risk Assessment', margins.left, yPos);
  yPos += 8;

  const riskLevel = calculateRiskLevel(data);
  const riskColors: Record<string, [number, number, number]> = {
    LOW: COLORS.success,
    MEDIUM: COLORS.warning,
    HIGH: COLORS.danger,
  };

  // Risk indicator box
  const riskBoxWidth = 80;
  doc.setFillColor(...(riskColors[riskLevel.level] || COLORS.gray));
  doc.roundedRect(margins.left, yPos, riskBoxWidth, 20, 3, 3, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(`${riskLevel.level} RISK`, margins.left + riskBoxWidth / 2, yPos + 13, { align: 'center' });

  doc.setTextColor(...COLORS.dark);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(riskLevel.description, margins.left + riskBoxWidth + 10, yPos + 10, { maxWidth: 100 });
};

/**
 * Add BASIC analysis page
 */
const addBASICAnalysis = (
  doc: jsPDF,
  data: ReportData,
  margins: { left: number; right: number; top: number; bottom: number }
): void => {
  let yPos = margins.top;

  // Section header
  yPos = addSectionHeader(doc, 'BASIC SCORES ANALYSIS', yPos, margins.left);
  yPos += 10;

  doc.setFontSize(10);
  doc.setTextColor(...COLORS.dark);
  doc.setFont('helvetica', 'normal');
  doc.text(
    'The Behavior Analysis and Safety Improvement Categories (BASICs) are used by FMCSA to identify high-risk motor carriers.',
    margins.left,
    yPos,
    { maxWidth: 180 }
  );
  yPos += 12;

  if (data.basics && data.basics.length > 0) {
    // BASIC scores table
    const basicData = data.basics.map(b => {
      const basic = (b.basic || b) as any;
      const percentile = parseFloat(String(basic.basicsPercentile || '0')) || 0;
      const exceeded = basic.exceededFMCSAInterventionThreshold === 'Y';

      return [
        basic.basicsType?.basicsShortDesc || 'Unknown',
        `${percentile.toFixed(1)}%`,
        basic.basicsViolationThreshold || 'N/A',
        exceeded ? 'YES' : 'No',
        basic.totalInspectionWithViolation?.toString() || '0',
        basic.totalViolation?.toString() || '0',
      ];
    });

    autoTable(doc, {
      startY: yPos,
      head: [['BASIC Category', 'Percentile', 'Threshold', 'Exceeded', 'Insp w/Viol', 'Total Viol']],
      body: basicData,
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: COLORS.primary, textColor: [255, 255, 255] },
      columnStyles: {
        1: { halign: 'center' },
        2: { halign: 'center' },
        3: { halign: 'center' },
        4: { halign: 'center' },
        5: { halign: 'center' },
      },
      didParseCell: (data) => {
        // Highlight exceeded thresholds
        if (data.section === 'body' && data.column.index === 3 && data.cell.raw === 'YES') {
          data.cell.styles.textColor = COLORS.danger;
          data.cell.styles.fontStyle = 'bold';
        }
      },
    });

    yPos = doc.lastAutoTable.finalY + 15;

    // BASIC explanations
    doc.setFontSize(11);
    doc.setTextColor(...COLORS.primary);
    doc.setFont('helvetica', 'bold');
    doc.text('BASIC Category Definitions', margins.left, yPos);
    yPos += 6;

    const basicDescriptions = [
      ['Unsafe Driving', 'Operation of CMVs in a dangerous or careless manner.'],
      ['Hours of Service', 'Operating a CMV while ill or fatigued; exceeding hours-of-service.'],
      ['Driver Fitness', 'Operating a CMV while unfit due to lack of training, experience, or medical issues.'],
      ['Controlled Substances', 'Operation of a CMV while impaired due to alcohol, illegal drugs, or misuse of medications.'],
      ['Vehicle Maintenance', 'Failure to properly maintain a CMV or improper loading of cargo.'],
      ['Hazmat Compliance', 'Unsafe handling of hazardous materials.'],
      ['Crash Indicator', 'Histories or patterns of high crash involvement.'],
    ];

    autoTable(doc, {
      startY: yPos,
      head: [],
      body: basicDescriptions,
      theme: 'plain',
      styles: { fontSize: 8, cellPadding: 2 },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 40 },
        1: { cellWidth: 140 },
      },
    });
  } else {
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.gray);
    doc.text('No BASIC score data available for this carrier.', margins.left, yPos);
  }
};

/**
 * Add violation analysis page
 */
const addViolationAnalysis = (
  doc: jsPDF,
  data: ReportData,
  margins: { left: number; right: number; top: number; bottom: number }
): void => {
  let yPos = margins.top;

  // Section header
  yPos = addSectionHeader(doc, 'VIOLATION ANALYSIS', yPos, margins.left);
  yPos += 10;

  // Violation summary by severity
  const severityCounts = {
    CRITICAL: data.violations?.filter(v => v.severity === 'CRITICAL').length || 0,
    HIGH: data.violations?.filter(v => v.severity === 'HIGH').length || 0,
    MEDIUM: data.violations?.filter(v => v.severity === 'MEDIUM').length || 0,
    LOW: data.violations?.filter(v => v.severity === 'LOW').length || 0,
  };

  doc.setFontSize(12);
  doc.setTextColor(...COLORS.primary);
  doc.setFont('helvetica', 'bold');
  doc.text('Violations by Severity', margins.left, yPos);
  yPos += 6;

  const severityData = [
    ['CRITICAL', severityCounts.CRITICAL.toString(), 'Immediate safety hazard'],
    ['HIGH', severityCounts.HIGH.toString(), 'Significant safety concern'],
    ['MEDIUM', severityCounts.MEDIUM.toString(), 'Moderate safety concern'],
    ['LOW', severityCounts.LOW.toString(), 'Minor safety concern'],
  ];

  autoTable(doc, {
    startY: yPos,
    head: [['Severity', 'Count', 'Description']],
    body: severityData,
    styles: { fontSize: 9, cellPadding: 3 },
    headStyles: { fillColor: COLORS.danger, textColor: [255, 255, 255] },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 30 },
      1: { halign: 'center', cellWidth: 20 },
      2: { cellWidth: 80 },
    },
    didParseCell: (data) => {
      if (data.section === 'body' && data.column.index === 0) {
        const severityColors: Record<string, [number, number, number]> = {
          CRITICAL: COLORS.danger,
          HIGH: [249, 115, 22], // Orange
          MEDIUM: COLORS.warning,
          LOW: COLORS.success,
        };
        const color = severityColors[data.cell.raw as string];
        if (color) {
          data.cell.styles.textColor = color;
        }
      }
    },
  });

  yPos = doc.lastAutoTable.finalY + 12;

  // Violations by BASIC category
  doc.setFontSize(12);
  doc.setTextColor(...COLORS.primary);
  doc.setFont('helvetica', 'bold');
  doc.text('Violations by BASIC Category', margins.left, yPos);
  yPos += 6;

  const basicCounts = data.violations?.reduce((acc: Record<string, number>, v) => {
    const cat = v.basicCategory || 'Unknown';
    acc[cat] = (acc[cat] || 0) + 1;
    return acc;
  }, {}) || {};

  const basicData = Object.entries(basicCounts)
    .sort((a, b) => (b[1] as number) - (a[1] as number))
    .map(([category, count]) => [category, count.toString()]);

  if (basicData.length > 0) {
    autoTable(doc, {
      startY: yPos,
      head: [['BASIC Category', 'Violation Count']],
      body: basicData,
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: COLORS.primary, textColor: [255, 255, 255] },
      columnStyles: {
        0: { cellWidth: 60 },
        1: { halign: 'center', cellWidth: 40 },
      },
    });

    yPos = doc.lastAutoTable.finalY + 12;
  }

  // Top 10 most common violations
  doc.setFontSize(12);
  doc.setTextColor(...COLORS.primary);
  doc.setFont('helvetica', 'bold');
  doc.text('Most Common Violation Types', margins.left, yPos);
  yPos += 6;

  const violationCounts = data.violations?.reduce((acc: Record<string, { count: number; description: string }>, v) => {
    const code = v.violationCode || 'Unknown';
    if (!acc[code]) {
      acc[code] = { count: 0, description: v.description || 'No description' };
    }
    acc[code].count++;
    return acc;
  }, {}) || {};

  type ViolationCountEntry = { count: number; description: string };
  const topViolations = Object.entries(violationCounts)
    .sort((a, b) => (b[1] as ViolationCountEntry).count - (a[1] as ViolationCountEntry).count)
    .slice(0, 10)
    .map(([code, data]) => {
      const { count, description } = data as ViolationCountEntry;
      return [
        code,
        count.toString(),
        description.length > 60 ? description.substring(0, 57) + '...' : description
      ];
    });

  if (topViolations.length > 0) {
    autoTable(doc, {
      startY: yPos,
      head: [['Code', 'Count', 'Description']],
      body: topViolations,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: COLORS.primary, textColor: [255, 255, 255] },
      columnStyles: {
        0: { cellWidth: 25, fontStyle: 'bold' },
        1: { halign: 'center', cellWidth: 15 },
        2: { cellWidth: 120 },
      },
    });
  }
};

/**
 * Add crash history page
 */
const addCrashHistory = (
  doc: jsPDF,
  data: ReportData,
  margins: { left: number; right: number; top: number; bottom: number }
): void => {
  let yPos = margins.top;

  // Section header
  yPos = addSectionHeader(doc, 'CRASH HISTORY', yPos, margins.left);
  yPos += 10;

  // Crash summary
  const crashes = data.crashes || [];
  const fatalCrashes = crashes.filter(c => Number(c.fatalities || 0) > 0);
  const injuryCrashes = crashes.filter(c => Number(c.injuries || 0) > 0 && Number(c.fatalities || 0) === 0);
  const towAwayCrashes = crashes.filter(c => c.towAway === 'Y' && Number(c.injuries || 0) === 0 && Number(c.fatalities || 0) === 0);
  const propertyOnlyCrashes = crashes.filter(c => c.towAway !== 'Y' && Number(c.injuries || 0) === 0 && Number(c.fatalities || 0) === 0);

  doc.setFontSize(12);
  doc.setTextColor(...COLORS.primary);
  doc.setFont('helvetica', 'bold');
  doc.text('Crash Summary', margins.left, yPos);
  yPos += 6;

  const crashSummary = [
    ['Total Crashes', crashes.length.toString()],
    ['Fatal Crashes', fatalCrashes.length.toString()],
    ['Injury Crashes', injuryCrashes.length.toString()],
    ['Tow-Away Crashes', towAwayCrashes.length.toString()],
    ['Property Damage Only', propertyOnlyCrashes.length.toString()],
  ];

  autoTable(doc, {
    startY: yPos,
    head: [['Category', 'Count']],
    body: crashSummary,
    styles: { fontSize: 9, cellPadding: 3 },
    headStyles: { fillColor: COLORS.danger, textColor: [255, 255, 255] },
    columnStyles: {
      0: { cellWidth: 60 },
      1: { halign: 'center', cellWidth: 30 },
    },
  });

  yPos = doc.lastAutoTable.finalY + 12;

  // Recent crashes table (up to 15)
  if (crashes.length > 0) {
    doc.setFontSize(12);
    doc.setTextColor(...COLORS.primary);
    doc.setFont('helvetica', 'bold');
    doc.text('Recent Crash Details', margins.left, yPos);
    yPos += 6;

    const recentCrashes = crashes.slice(0, 15).map(c => [
      formatDate(c.reportDate),
      c.reportState || 'N/A',
      c.city || 'N/A',
      c.fatalities?.toString() || '0',
      c.injuries?.toString() || '0',
      c.towAway === 'Y' ? 'Yes' : 'No',
      c.hazmatReleased === 'Y' ? 'Yes' : 'No',
    ]);

    autoTable(doc, {
      startY: yPos,
      head: [['Date', 'State', 'City', 'Fatal', 'Injuries', 'Tow', 'Hazmat']],
      body: recentCrashes,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: COLORS.primary, textColor: [255, 255, 255] },
      columnStyles: {
        0: { cellWidth: 22 },
        1: { cellWidth: 15, halign: 'center' },
        2: { cellWidth: 35 },
        3: { cellWidth: 15, halign: 'center' },
        4: { cellWidth: 18, halign: 'center' },
        5: { cellWidth: 15, halign: 'center' },
        6: { cellWidth: 18, halign: 'center' },
      },
      didParseCell: (data) => {
        // Highlight fatal crashes
        if (data.section === 'body' && data.column.index === 3 && parseInt(data.cell.raw as string) > 0) {
          data.cell.styles.textColor = COLORS.danger;
          data.cell.styles.fontStyle = 'bold';
        }
        // Highlight injury crashes
        if (data.section === 'body' && data.column.index === 4 && parseInt(data.cell.raw as string) > 0) {
          data.cell.styles.textColor = COLORS.warning;
          data.cell.styles.fontStyle = 'bold';
        }
      },
    });

    if (crashes.length > 15) {
      yPos = doc.lastAutoTable.finalY + 5;
      doc.setFontSize(9);
      doc.setTextColor(...COLORS.gray);
      doc.text(`Showing 15 of ${crashes.length} total crashes. Export full crash report for complete details.`, margins.left, yPos);
    }
  } else {
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.success);
    doc.setFont('helvetica', 'bold');
    doc.text('No crash records found for this carrier.', margins.left, yPos);
  }
};

/**
 * Add inspection summary page
 */
const addInspectionSummary = (
  doc: jsPDF,
  data: ReportData,
  margins: { left: number; right: number; top: number; bottom: number }
): void => {
  let yPos = margins.top;

  // Section header
  yPos = addSectionHeader(doc, 'INSPECTION SUMMARY', yPos, margins.left);
  yPos += 10;

  const inspections = data.inspections || [];
  const profile = data.profile || {};

  // Inspection statistics
  doc.setFontSize(12);
  doc.setTextColor(...COLORS.primary);
  doc.setFont('helvetica', 'bold');
  doc.text('Inspection Statistics', margins.left, yPos);
  yPos += 6;

  const withViolations = inspections.filter(i => (i.violTotal || 0) > 0).length;
  const cleanInspections = inspections.length - withViolations;
  const oosInspections = inspections.filter(i => (i.oosTotal || 0) > 0).length;

  const inspStats = [
    ['Total Inspections (Loaded)', inspections.length.toString()],
    ['Clean Inspections', cleanInspections.toString()],
    ['Inspections with Violations', withViolations.toString()],
    ['Out-of-Service Inspections', oosInspections.toString()],
    ['Driver OOS Rate', `${profile.driverOosRate || 0}% (National Avg: ${profile.driverOosRateNationalAverage || '5.51%'})`],
    ['Vehicle OOS Rate', `${profile.vehicleOosRate || 0}% (National Avg: ${profile.vehicleOosRateNationalAverage || '20.72%'})`],
  ];

  autoTable(doc, {
    startY: yPos,
    head: [],
    body: inspStats,
    theme: 'striped',
    styles: { fontSize: 9, cellPadding: 3 },
    columnStyles: {
      0: { cellWidth: 70 },
      1: { cellWidth: 80 },
    },
    alternateRowStyles: { fillColor: COLORS.lightGray },
  });

  yPos = doc.lastAutoTable.finalY + 12;

  // Inspections by level
  doc.setFontSize(12);
  doc.setTextColor(...COLORS.primary);
  doc.setFont('helvetica', 'bold');
  doc.text('Inspections by Level', margins.left, yPos);
  yPos += 6;

  const levelCounts: Record<string, number> = {};
  inspections.forEach(i => {
    const level = i.inspLevelId || 'Unknown';
    levelCounts[level] = (levelCounts[level] || 0) + 1;
  });

  const levelDescriptions: Record<string, string> = {
    '1': 'Full inspection (driver + vehicle)',
    '2': 'Walk-around driver/vehicle inspection',
    '3': 'Driver-only inspection',
    '4': 'Special inspections',
    '5': 'Vehicle-only inspection',
    '6': 'Enhanced NAS inspection',
  };

  const levelData = Object.entries(levelCounts)
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([level, count]) => [
      `Level ${level}`,
      count.toString(),
      levelDescriptions[level] || 'Other inspection type'
    ]);

  if (levelData.length > 0) {
    autoTable(doc, {
      startY: yPos,
      head: [['Level', 'Count', 'Description']],
      body: levelData,
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: COLORS.primary, textColor: [255, 255, 255] },
      columnStyles: {
        0: { cellWidth: 25 },
        1: { halign: 'center', cellWidth: 20 },
        2: { cellWidth: 90 },
      },
    });

    yPos = doc.lastAutoTable.finalY + 12;
  }

  // Inspections by state
  doc.setFontSize(12);
  doc.setTextColor(...COLORS.primary);
  doc.setFont('helvetica', 'bold');
  doc.text('Inspections by State (Top 10)', margins.left, yPos);
  yPos += 6;

  const stateCounts: Record<string, number> = {};
  inspections.forEach(i => {
    const state = String(i.reportState || i.reportState || 'Unknown');
    stateCounts[state] = (stateCounts[state] || 0) + 1;
  });

  const stateData = Object.entries(stateCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10)
    .map(([state, count]) => [state, count.toString()]);

  if (stateData.length > 0) {
    autoTable(doc, {
      startY: yPos,
      head: [['State', 'Inspection Count']],
      body: stateData,
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: COLORS.primary, textColor: [255, 255, 255] },
      columnStyles: {
        0: { cellWidth: 30 },
        1: { halign: 'center', cellWidth: 40 },
      },
    });
  }
};

/**
 * Add recommendations page
 */
const addRecommendations = (
  doc: jsPDF,
  data: ReportData,
  margins: { left: number; right: number; top: number; bottom: number }
): void => {
  let yPos = margins.top;

  // Section header
  yPos = addSectionHeader(doc, 'RECOMMENDATIONS & NEXT STEPS', yPos, margins.left);
  yPos += 10;

  const recommendations = generateRecommendations(data);

  recommendations.forEach((rec, index) => {
    // Priority badge
    const priorityColors: Record<string, [number, number, number]> = {
      HIGH: COLORS.danger,
      MEDIUM: COLORS.warning,
      LOW: COLORS.success,
    };

    doc.setFillColor(...(priorityColors[rec.priority] || COLORS.gray));
    doc.roundedRect(margins.left, yPos - 4, 35, 7, 1.5, 1.5, 'F');
    doc.setFontSize(7);
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.text(`${rec.priority} PRIORITY`, margins.left + 17.5, yPos, { align: 'center' });

    // Recommendation title
    doc.setFontSize(11);
    doc.setTextColor(...COLORS.dark);
    doc.setFont('helvetica', 'bold');
    doc.text(`${index + 1}. ${rec.title}`, margins.left + 40, yPos);
    yPos += 6;

    // Recommendation description
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.gray);
    doc.setFont('helvetica', 'normal');
    const lines = doc.splitTextToSize(rec.description, 150);
    doc.text(lines, margins.left + 5, yPos);
    yPos += lines.length * 4 + 8;
  });

  // Disclaimer
  yPos += 10;
  doc.setFillColor(...COLORS.lightGray);
  doc.roundedRect(margins.left, yPos, 180, 25, 2, 2, 'F');

  doc.setFontSize(8);
  doc.setTextColor(...COLORS.gray);
  doc.setFont('helvetica', 'italic');
  doc.text(
    'DISCLAIMER: This report is for informational purposes only and should not be considered legal or compliance advice. ' +
    'Always consult with qualified safety professionals and refer to current FMCSA regulations for compliance decisions.',
    margins.left + 5,
    yPos + 8,
    { maxWidth: 170 }
  );
};

// ==========================================
// HELPER FUNCTIONS
// ==========================================

/**
 * Add section header
 */
const addSectionHeader = (
  doc: jsPDF,
  title: string,
  yPos: number,
  leftMargin: number
): number => {
  const pageWidth = doc.internal.pageSize.getWidth();

  doc.setFillColor(...COLORS.primary);
  doc.rect(0, yPos - 5, pageWidth, 12, 'F');

  doc.setFontSize(14);
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.text(title, leftMargin, yPos + 3);

  return yPos + 12;
};

/**
 * Add page numbers to all pages
 */
const addPageNumbers = (doc: jsPDF): void => {
  const pageCount = doc.getNumberOfPages();
  const pageHeight = doc.internal.pageSize.getHeight();
  const pageWidth = doc.internal.pageSize.getWidth();

  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.gray);
    doc.setFont('helvetica', 'normal');

    // Page number
    doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' });

    // Footer text (skip cover page)
    if (i > 1) {
      doc.text('Transportation Safety Platform - Confidential', 14, pageHeight - 10);
    }
  }
};

/**
 * Calculate risk level based on data
 */
const calculateRiskLevel = (data: ReportData): { level: string; description: string } => {
  const violations = data.violations || [];
  const crashes = data.crashes || [];
  const profile = data.profile;

  let riskScore = 0;
  const issues: string[] = [];

  // Check for critical violations
  const criticalCount = violations.filter(v => v.severity === 'CRITICAL').length;
  if (criticalCount > 10) {
    riskScore += 3;
    issues.push(`${criticalCount} critical violations`);
  } else if (criticalCount > 5) {
    riskScore += 2;
    issues.push(`${criticalCount} critical violations`);
  } else if (criticalCount > 0) {
    riskScore += 1;
  }

  // Check for fatal crashes
  const fatalCrashes = crashes.filter(c => c.fatalities > 0).length;
  if (fatalCrashes > 0) {
    riskScore += 3;
    issues.push(`${fatalCrashes} fatal crash(es)`);
  }

  // Check OOS rates
  const driverOosRate = parseFloat(String(profile.driverOosRate || '0'));
  const vehicleOosRate = parseFloat(String(profile.vehicleOosRate || '0'));

  if (driverOosRate > 15) {
    riskScore += 2;
    issues.push('High driver OOS rate');
  } else if (driverOosRate > 10) {
    riskScore += 1;
  }

  if (vehicleOosRate > 40) {
    riskScore += 2;
    issues.push('High vehicle OOS rate');
  } else if (vehicleOosRate > 30) {
    riskScore += 1;
  }

  // Check BASIC thresholds
  const exceededBasics = (data.basics || []).filter(
    b => (b.basic as Record<string, unknown>)?.exceededFMCSAInterventionThreshold === 'Y'
  ).length;
  if (exceededBasics > 2) {
    riskScore += 3;
    issues.push(`${exceededBasics} BASIC thresholds exceeded`);
  } else if (exceededBasics > 0) {
    riskScore += exceededBasics;
  }

  // Determine level
  if (riskScore >= 6) {
    return {
      level: 'HIGH',
      description: `Carrier presents significant safety concerns. Key issues: ${issues.slice(0, 3).join(', ') || 'Multiple risk factors identified'}.`,
    };
  } else if (riskScore >= 3) {
    return {
      level: 'MEDIUM',
      description: `Carrier has moderate safety concerns requiring attention. ${issues.length > 0 ? `Notable: ${issues[0]}` : 'Review recommended.'}.`,
    };
  }
  return {
    level: 'LOW',
    description: 'Carrier demonstrates satisfactory safety performance. Continue regular monitoring.',
  };
};

/**
 * Generate recommendations based on data
 */
const generateRecommendations = (data: ReportData): Array<{ priority: string; title: string; description: string }> => {
  const recommendations: Array<{ priority: string; title: string; description: string }> = [];
  const violations = data.violations || [];
  const crashes = data.crashes || [];
  const profile = data.profile;
  const basics = data.basics || [];

  // Check for critical violations
  const criticalViolations = violations.filter(v => v.severity === 'CRITICAL');
  if (criticalViolations.length > 5) {
    recommendations.push({
      priority: 'HIGH',
      title: 'Address Critical Violations Immediately',
      description: `Found ${criticalViolations.length} critical violations in recent inspections. These represent immediate safety hazards and should be addressed through driver training, vehicle maintenance, and operational procedure reviews.`,
    });
  }

  // Check for fatal crashes
  const fatalCrashes = crashes.filter(c => Number(c.fatalities || 0) > 0);
  if (fatalCrashes.length > 0) {
    recommendations.push({
      priority: 'HIGH',
      title: 'Review Fatal Crash History',
      description: `Carrier has ${fatalCrashes.length} fatal crash(es) on record. Conduct thorough root cause analysis and implement corrective actions to prevent future incidents.`,
    });
  }

  // Check BASIC thresholds
  const exceededBasics = basics.filter(b => (b.basic as Record<string, unknown>)?.exceededFMCSAInterventionThreshold === 'Y');
  if (exceededBasics.length > 0) {
    const categories = exceededBasics.map(b => ((b.basic as Record<string, unknown>)?.basicsType as Record<string, unknown>)?.basicsShortDesc || 'Unknown').join(', ');
    recommendations.push({
      priority: 'HIGH',
      title: 'BASIC Threshold Intervention Required',
      description: `Carrier has exceeded FMCSA intervention thresholds in: ${categories}. Immediate action is required to avoid potential enforcement actions.`,
    });
  }

  // Check driver OOS rate
  const driverOosRate = parseFloat(String(profile.driverOosRate || '0'));
  if (driverOosRate > 10) {
    recommendations.push({
      priority: 'MEDIUM',
      title: 'Reduce Driver Out-of-Service Rate',
      description: `Driver OOS rate of ${driverOosRate.toFixed(1)}% is above the national average. Review hours-of-service compliance, driver qualifications, and medical certifications.`,
    });
  }

  // Check vehicle OOS rate
  const vehicleOosRate = parseFloat(String(profile.vehicleOosRate || '0'));
  if (vehicleOosRate > 30) {
    recommendations.push({
      priority: 'MEDIUM',
      title: 'Improve Vehicle Maintenance Program',
      description: `Vehicle OOS rate of ${vehicleOosRate.toFixed(1)}% indicates maintenance issues. Enhance preventive maintenance schedules and pre-trip inspection procedures.`,
    });
  }

  // Vehicle maintenance violations
  const maintenanceViolations = violations.filter(v =>
    String(v.basicCategory || '').toLowerCase().includes('maintenance') ||
    String(v.basicCategory || '').toLowerCase().includes('vehicle')
  );
  if (maintenanceViolations.length > 20) {
    recommendations.push({
      priority: 'MEDIUM',
      title: 'Strengthen Vehicle Maintenance Procedures',
      description: `${maintenanceViolations.length} vehicle maintenance-related violations detected. Implement comprehensive maintenance tracking and driver pre-trip inspection training.`,
    });
  }

  // Hours of service violations
  const hosViolations = violations.filter(v =>
    String(v.basicCategory || '').toLowerCase().includes('hours') ||
    String(v.basicCategory || '').toLowerCase().includes('hos')
  );
  if (hosViolations.length > 10) {
    recommendations.push({
      priority: 'MEDIUM',
      title: 'Enhance Hours of Service Compliance',
      description: `${hosViolations.length} HOS-related violations found. Review scheduling practices, implement ELD compliance training, and ensure adequate rest periods.`,
    });
  }

  // Default recommendations if none generated
  if (recommendations.length === 0) {
    recommendations.push({
      priority: 'LOW',
      title: 'Maintain Current Safety Standards',
      description: 'Continue current safety practices and maintain regular monitoring. Consider periodic safety audits to identify improvement opportunities.',
    });
  }

  // Always add monitoring recommendation
  recommendations.push({
    priority: 'LOW',
    title: 'Set Up Continuous Monitoring',
    description: 'Enable real-time alerts for this carrier to receive immediate notifications of new violations, crashes, or BASIC score changes.',
  });

  return recommendations.slice(0, 6); // Limit to 6 recommendations
};

/**
 * Format date helper
 */
const formatDate = (value: unknown): string => {
  if (!value) return 'N/A';
  try {
    const date = new Date(value as string | number | Date);
    if (isNaN(date.getTime())) return String(value);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  } catch {
    return String(value);
  }
};
